// Section 19: events in effect contribute through their EventDef.modifiers hook, looked up
// through the registry (content never imports content). An effect counts from its `sinceTurn`
// to its `untilTurn` inclusive. Stream E2 also contributes here, in the events slot:
//   Section 14 emigration pressure, the D-15 terms (each its own row so the stated reason is the
//     largest): 0.4 x (100 - happiness), 0.4 x (100 - trust), 10 x safety deficit, 5 per hungry
//     season (children and guests have none);
//   Section 14 guests are fed at half rations (food demand 0.5x) while their stay lasts;
//   Sections 11 and 19 improvements once adopted: crop rotation harvest +10%, better kiln build
//     cost 0.8x (E2-20), granary lining: food in a sound granary spoils 1% instead of 3%.
// Constants: config.migration, config.events['improvement-proposal'], config.buildings.granary.
import { defineContributor } from '../../core/registry';
import { TARGET_KINDS, mod } from '../../core/modifiers';
import { memoByState } from '../../core/memo';
import { buildingsOf, needsOf, settlementTrust } from '../../core/queries';
import type { ImprovementId, Modifier, PersonId, ReadContext, TargetKind } from '../../core/types';

function adopted(ctx: ReadContext, id: ImprovementId): boolean {
  return ctx.state.improvements.some((i) => i.id === id);
}

function pressureTerms(ctx: ReadContext, personId: PersonId): Modifier[] {
  const p = ctx.state.people[personId];
  if (p === undefined || p.ageSeasons < ctx.cfg.people.adultAtSeasons || p.status.guest !== null)
    return [];
  const m = ctx.cfg.migration;
  const max = ctx.cfg.people.statMax;
  const safety = needsOf(ctx.state, personId).safety;
  const out: Modifier[] = [];
  const add = (source: string, value: number, note: string): void => {
    if (value > 0) out.push(mod.add(source, value, note));
  };
  add('migration:unhappy', m.pressureHappinessWeight * (max - p.happiness), 'unhappy');
  // M7: Section 16 "below 40 ... emigration starts": pressure rises with each point a person's
  // trust, and the settlement's, is below that line.
  const settlement = memoByState(ctx.state, 'settlement-trust', () => settlementTrust(ctx.state));
  add(
    'migration:distrust',
    m.pressureTrustWeight * (max - p.trust) +
      m.pressureLowTrustWeight * Math.max(0, m.pressureLowTrustBelow - p.trust) +
      m.pressureLowSettlementWeight * Math.max(0, m.pressureLowTrustBelow - settlement),
    'distrust',
  );
  add('migration:unsafe', m.pressureSafetyWeight * (1 - safety), 'unsafe');
  add(
    'migration:hunger',
    m.pressureHungerPerSeason * p.streaks.hungry,
    `hungry ${p.streaks.hungry} seasons`,
  );
  return out;
}

/** Section 7 and 9 granary spoilage recomputed with the lining (sound granaries only). */
function linedGranary(ctx: ReadContext): Modifier[] {
  const c = ctx.cfg.buildings.granary;
  const granaries = buildingsOf(ctx.state, 'granary');
  const amount = ctx.state.stocks.food.amount + ctx.state.seedReserved;
  if (granaries.length === 0 || amount <= 0) return [];
  const lining = ctx.cfg.events['improvement-proposal'].liningSpoilage;
  let remaining = amount;
  let spoiled = 0;
  for (const g of granaries) {
    const held = Math.min(remaining, c.foodCapacity);
    const damaged = g.condition < ctx.cfg.building.fullEffectCondition;
    spoiled += held * (damaged ? c.damagedSpoilage : lining);
    remaining -= held;
  }
  spoiled += remaining * ctx.cfg.resources.spoilage.food;
  return [mod.set('improvement:granary-lining', spoiled / amount, 'lined granary')];
}

/** Target kinds this contributor adds rows to itself (the switch below). */
const OWN_KINDS: ReadonlySet<TargetKind> = new Set([
  'emigrationPressure',
  'demand',
  'harvestYield',
  'buildCost',
  'spoilage',
]);
const NONE: readonly Modifier[] = Object.freeze([]);

export const eventsContributor = defineContributor({
  slot: 'events',
  id: 'events:in-effect',
  targets: TARGET_KINDS,
  modifiers(ctx, target) {
    const turn = ctx.season.turn;
    let out: Modifier[] | null = null;
    for (const effect of ctx.state.activeEffects) {
      if (effect.untilTurn < turn || effect.sinceTurn > turn) continue;
      const def = ctx.registry.events[effect.eventId];
      if (!def.modifierTargets.includes(target.kind)) continue;
      for (const m of def.modifiers(ctx, target, effect)) (out ??= []).push(m);
    }
    // Most targets have nothing below: answer without allocating (Section 2 budget).
    if (!OWN_KINDS.has(target.kind)) return out ?? NONE;
    out ??= [];
    switch (target.kind) {
      case 'emigrationPressure':
        out.push(...pressureTerms(ctx, target.personId));
        break;
      case 'demand':
        if (target.resource === 'food' && ctx.state.people[target.personId]?.status.guest)
          out.push(mod.mul('migration:guest', ctx.cfg.migration.guestRation, 'guest'));
        break;
      case 'harvestYield':
        if (adopted(ctx, 'crop-rotation'))
          out.push(
            mod.mul(
              'improvement:crop-rotation',
              1 + ctx.cfg.events['improvement-proposal'].yieldBonus,
              'crop rotation',
            ),
          );
        break;
      case 'buildCost':
        if (adopted(ctx, 'better-kiln'))
          out.push(
            mod.mul(
              'improvement:better-kiln',
              ctx.cfg.events['improvement-proposal'].kilnCostMul,
              'better kiln',
            ),
          );
        break;
      case 'spoilage':
        if (target.resource === 'food' && adopted(ctx, 'granary-lining'))
          out.push(...linedGranary(ctx));
        break;
      default:
        break;
    }
    return out;
  },
});
